import { Stack, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { EmptyState, PerformanceChart } from "@/components/elements";
import { getAllMakeNames } from "@/lib/makes";
import { usePlansUser } from "@/services/plans/get-user-plan";
import { tooltipFormatter } from "@/utils/tooltipFormatter";

import type { MakeName } from "@/types/make";
import type { PerformanceChart as IPerformanceChart } from "@/types/performance";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

export type MarketPerformanceChartProps = {
  benchmarks?: IPerformanceChart[];
  makeOrDecade: string;
  performance?: IPerformanceChart;
} & MantineStyleProps;

export const MarketPerformanceChart: FC<MarketPerformanceChartProps> = ({
  benchmarks = [],
  makeOrDecade,
  performance,
  ...props
}) => {
  const { t } = useTranslation();

  const makeNamesArray = getAllMakeNames();

  function isMakeName(value: string): value is MakeName {
    return (makeNamesArray as readonly string[]).includes(value);
  }

  const { data: plans } = usePlansUser();

  const title = isMakeName(makeOrDecade)
    ? t("market.make_performance")
    : t("market.decade_performance");

  return (
    <Stack {...props}>
      <Title order={4}>{title}</Title>

      {performance && performance.data.length > 0 ? (
        <PerformanceChart
          benchmarks={benchmarks}
          chartProps={{ h: 200 }}
          hideBenchmarkSelector
          performance={performance}
          hiddenBenchmarks={["superb", "excellent", "fair", "good"]}
          defaultBenchmark="superb"
          tooltipFormatter={tooltipFormatter}
          restrictKpisByPlan
          showLockerInLegend={plans?.config.auction_analytics === false}
        />
      ) : (
        <EmptyState type="noData" />
      )}
    </Stack>
  );
};
